/**
 * Per-option pricing in the Shopify Subscriptions widget.
 *
 * The app renders a price for each option (.in_widget_price) but hides it when
 * the theme already has .price-item--sale / .price-item--regular elements — in
 * that mode it drives the theme's main price instead. We want both: the main
 * price AND a price on each row, so CSS reveals those elements (see theme.css)
 * and this script adds the struck-through compare-at price beside the
 * discounted one.
 *
 * The compare-at value only exists as a data attribute on the radio inputs and
 * <option>s, never as an element, so it has to be injected. It is inserted as a
 * SIBLING of .in_widget_price rather than a child, because the app rewrites
 * that element's innerHTML whenever the delivery frequency changes.
 */
(() => {
  const WAS_CLASS = 'pw-was-price';

  const clean = (v) => (v || '').trim();

  function syncRow(wrapper) {
    const radio = wrapper.querySelector('input[type="radio"]');
    const priceEl = wrapper.querySelector('.in_widget_price');
    if (!radio || !priceEl) return;

    // For a subscription row the live compare-at lives on the selected <option>;
    // fall back to the radio for the one-time row.
    let compare = clean(radio.dataset.variantCompareAtPrice);
    const group = wrapper.closest('.subscription_group');
    const dropdown = group && group.querySelector('.group_dropdown');
    if (dropdown && dropdown.selectedIndex > -1) {
      const opt = dropdown.options[dropdown.selectedIndex];
      if (opt && clean(opt.dataset.variantCompareAtPrice)) {
        compare = clean(opt.dataset.variantCompareAtPrice);
      }
    }

    const existing = wrapper.querySelector('.' + WAS_CLASS);
    // nothing to show when there is no discount on this row
    if (!compare || compare === clean(priceEl.textContent)) {
      if (existing) existing.remove();
      return;
    }

    const el = existing || document.createElement('span');
    if (!existing) {
      el.className = WAS_CLASS;
      // decorative: the accessible price is already announced via aria-label
      el.setAttribute('aria-hidden', 'true');
      priceEl.insertAdjacentElement('afterend', el);
    }
    if (clean(el.textContent) !== compare) el.textContent = compare;
  }

  /**
   * The app renders the policy line as a sibling of .plan_card, outside the
   * card entirely. It only describes the subscription, so move it inside the
   * subscribe group, directly beneath the delivery dropdown. Assumes a single
   * selling-plan group (the first dropdown); the app's own JS never references
   * this element, so relocating it is safe.
   */
  function placePolicy(container) {
    const policy = container.querySelector('.shopify_subscriptions_app_policy');
    const dropdown = container.querySelector('.group_dropdown');
    if (!policy || !dropdown) return;

    const group = dropdown.closest('.subscription_group');
    if (!group) return;

    // already in place — nothing to do (keeps this idempotent across re-renders)
    if (policy.previousElementSibling === dropdown) return;

    dropdown.insertAdjacentElement('afterend', policy);
    policy.classList.add('pw-policy-inline');
  }

  /* ── main product price ──────────────────────────────────────────────────
     The app only drives the theme's price when the theme exposes
     .price-item--regular AND .price-item--sale. This theme uses neither, so the
     app stays in widget-price mode and the headline price never moves. Drive it
     here instead: the subscription price with the original struck out beside it.
     Scoped to the main product block and the sticky bar so product cards
     elsewhere on the page are never touched. */

  let basePrice = null; // the one-time price, in the theme's own money format

  function priceTargets() {
    return [...document.querySelectorAll('.price-container')]
      .filter(
        (c) =>
          c.closest('.product-information--main') ||
          c.closest('sticky-add-to-cart, [class*="sticky-add-to-cart"]')
      )
      .map((c) => ({ sale: c.querySelector('.price--sale'), compare: c.querySelector('.compare-at-price') }))
      .filter((n) => n.sale);
  }

  // the app formats money with the currency code ("$121.50 USD"), the theme
  // without ("$121.50") — match whichever the theme is already using
  function matchMoneyFormat(value, sample) {
    const v = clean(value);
    if (!v) return '';
    if (/[A-Z]{3}$/.test(clean(sample))) return v;
    return v.replace(/\s*[A-Z]{3}$/, '');
  }

  function updateMainPrice(container) {
    const targets = priceTargets();
    if (!targets.length) return;

    const checked = container.querySelector('input[name^="purchaseOption"]:checked');
    if (!checked) return;

    let price = basePrice;
    let compare = '';

    if (checked.dataset.radioType === 'selling_plan_group') {
      const group = checked.closest('.subscription_group');
      const dropdown = group && group.querySelector('.group_dropdown');
      const opt = dropdown && dropdown.selectedIndex > -1 ? dropdown.options[dropdown.selectedIndex] : null;
      price = matchMoneyFormat(
        (opt && opt.dataset.variantPrice) || checked.dataset.variantPrice,
        basePrice
      );
      compare = matchMoneyFormat(
        (opt && opt.dataset.variantCompareAtPrice) || checked.dataset.variantCompareAtPrice,
        basePrice
      );
      if (compare === price) compare = '';
    }

    targets.forEach(({ sale, compare: compareEl }) => {
      if (price && clean(sale.textContent) !== price) sale.textContent = price;
      if (compareEl && clean(compareEl.textContent) !== compare) compareEl.textContent = compare;
    });
  }

  function sync(container) {
    container
      .querySelectorAll('.title_and_price_wrapper')
      .forEach(syncRow);
    updateMainPrice(container);
  }

  /**
   * The app markup renders BOTH radios with the `checked` attribute; because
   * they share a name the browser honours the last one, so the subscription
   * ends up preselected. Select the one-time option instead. Clicking it (as
   * opposed to setting .checked) fires the events the app listens for, so it
   * clears the selling_plan input and updates its own state.
   *
   * Runs once per widget, on first bind — never fights a later user choice.
   */
  function preferOneTime(container) {
    const oneTime = container.querySelector('input[data-radio-type="one_time_purchase"]');
    if (!oneTime || oneTime.checked) return;
    oneTime.click();
  }

  function bind(container) {
    placePolicy(container);
    if (container.dataset.pwPriceBound) return;
    container.dataset.pwPriceBound = 'true';

    preferOneTime(container);

    // capture the untouched one-time price before anything is rewritten
    if (basePrice === null) {
      const oneTime = container.querySelector('input[data-radio-type="one_time_purchase"]');
      const first = priceTargets()[0];
      basePrice = first ? clean(first.sale.textContent) : '';
      if (!basePrice && oneTime) basePrice = matchMoneyFormat(oneTime.dataset.variantPrice, '');
    }

    container.addEventListener('change', () => requestAnimationFrame(() => sync(container)));
    sync(container);
  }

  function init() {
    document.querySelectorAll('.shopify_subscriptions_app_container').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // the app re-renders the whole section when the variant changes, which drops
  // both our injected node and the listener — pick the new container back up
  new MutationObserver(() => init()).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
